import { clsx } from 'clsx'
import type { Message } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import { wsManager } from '../../api/websocket'

// Telegram 7 emoji set
const TELEGRAM_7_EMOJIS = ['👍', '❤️', '🔥', '🥳', '😢', '👎', '😡']

interface MessageContextMenuProps {
  message: Message
  position: { x: number; y: number }
  onClose: () => void
  isLastUserMessage?: boolean
}

/**
 * Context menu for message actions (long-press on mobile, right-click on desktop).
 * Shows emoji row + action buttons.
 */
export default function MessageContextMenu({
  message,
  position,
  onClose,
  isLastUserMessage = false,
}: MessageContextMenuProps) {
  const setReplyingTo = useChatStore((state) => state.setReplyingTo)
  const setEditingMessage = useChatStore((state) => state.setEditingMessage)
  const isUser = message.role === 'user'

  const handleReaction = (emoji: string) => {
    wsManager.sendReaction(message.id, emoji)
    onClose()
  }

  const handleReply = () => {
    setReplyingTo(message)
    onClose()
  }

  const handleEdit = () => {
    setEditingMessage(message)
    onClose()
  }

  const handleDelete = () => {
    if (window.confirm('Удалить сообщение?')) {
      wsManager.deleteMessage(message.id)
    }
    onClose()
  }

  // Calculate position to keep menu on screen
  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - 220),
    top: Math.min(position.y, window.innerHeight - 200),
    zIndex: 50,
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault()
          onClose()
        }}
      />

      {/* Menu */}
      <div
        data-testid="context-menu"
        style={menuStyle}
        className="bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden animate-fade-in"
      >
        {/* Emoji reaction row */}
        <div className="flex items-center gap-1 p-2 border-b border-neutral-100">
          {TELEGRAM_7_EMOJIS.map((emoji) => {
            const isReacted = message.reactions?.some(
              (r) => r.emoji === emoji && r.reactedByMe
            )
            return (
              <button
                key={emoji}
                data-testid={`reaction-${emoji}`}
                onClick={() => handleReaction(emoji)}
                className={clsx(
                  'w-9 h-9 flex items-center justify-center rounded-full text-lg',
                  'hover:bg-neutral-100 transition-colors',
                  isReacted && 'bg-primary-100'
                )}
              >
                {emoji}
              </button>
            )
          })}
        </div>

        {/* Action buttons */}
        <div className="py-1">
          {/* Reply */}
          <button
            data-testid="action-reply"
            onClick={handleReply}
            className="w-full px-4 py-2.5 text-left text-sm hover:bg-neutral-50 flex items-center gap-3"
          >
            <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            <span>Ответить</span>
          </button>

          {/* Edit (only for user's last message) */}
          {isUser && isLastUserMessage && (
            <button
              data-testid="action-edit"
              onClick={handleEdit}
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-neutral-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Редактировать</span>
            </button>
          )}

          {/* Delete (only for user messages) */}
          {isUser && (
            <button
              data-testid="action-delete"
              onClick={handleDelete}
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Удалить</span>
            </button>
          )}
        </div>
      </div>
    </>
  )
}
